import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../../BottomSheet";
import { Button as ButtonV2 } from "../../Button";
import { CrossIcon, ErrorIconV2 } from "../../Icons";
import { ResponsiveModal } from "../../ResponsiveModal";
import { Typo } from "../../Typo";
import { withSupportLink } from "./supportLink";

type CreateProgramErrorModalProps = {
  open: boolean;
  onClose: () => void;
  isMobile?: boolean;
};

/**
 * Generic failure modal for the create-program pre-flight: the master status
 * read (GET /v1/master/status) errored, so the wizard cannot be opened and
 * neither blocking modal can be chosen — we do not know the rebate-account
 * state or the program count.
 *
 * Deliberately one message for every failure class, including 403 (not an
 * eligible Master). The caller cannot act differently on any of them, and
 * naming the cause would leak why they are ineligible.
 */
export const CreateProgramErrorModal = ({
  open,
  onClose,
  isMobile = false,
}: CreateProgramErrorModalProps) => {
  const { t } = useTranslation();
  const closeLabel = t("common_close", "Close");

  const closeButton = (
    <ButtonV2
      variant="brand"
      size="md"
      onClick={onClose}
      className={clsx(isMobile && "w-full")}
      data-testid="create_program_error_modal_close_button"
    >
      {closeLabel}
    </ButtonV2>
  );

  const illustration = (
    <ErrorIconV2 className="size-16" aria-hidden="true" focusable="false" />
  );

  const title = t("subIBReport_createErrorTitle", "Something went wrong");

  const body = t(
    "subIBReport_createErrorBody",
    "We couldn't create new rebate program right now. Try again later.",
  );

  const help = withSupportLink(
    t(
      "subIBReport_createErrorHelp",
      "If the issue persists, contact support@pepperstonepartners.com.",
    ),
  );

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={open}
        onClose={onClose}
        isFullScreen
        isDismissable={false}
        showCloseButton
        closeButtonTestId="create_program_error_modal_dismiss_button"
        contentContainerClassName="py-3"
        footer={closeButton}
      >
        <div
          className="flex flex-col gap-4 pb-1"
          role="alert"
          data-testid="create_program_error_modal"
        >
          {illustration}
          <Typo element="h2" type="headingLG" textColor="text-text-primary">
            {title}
          </Typo>
          <Typo element="p" type="bodySM" textColor="text-text-secondary">
            {body}
          </Typo>
          <Typo element="p" type="bodyXS" textColor="text-text-tertiary">
            {help}
          </Typo>
        </div>
      </BottomSheet>
    );
  }

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      isMobile={false}
      headerClassName="!hidden"
      // `!` beats ResponsiveModal's hardcoded `mt-6`.
      footerContainerClassName="!mt-8"
      footer={<div className="flex justify-end">{closeButton}</div>}
    >
      {/* Hiding the modal header also hides ResponsiveModal's own X, so the
          dismiss affordance is rendered here. */}
      <div
        className="flex flex-col gap-4"
        role="alert"
        data-testid="create_program_error_modal"
      >
        <div className="flex flex-col">
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={closeLabel}
              onClick={onClose}
              data-testid="create_program_error_modal_dismiss_button"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
            >
              <CrossIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          {illustration}
        </div>
        <Typo element="h2" type="headingXL" textColor="text-text-primary">
          {title}
        </Typo>
        <Typo element="p" type="bodyMD" textColor="text-text-secondary">
          {body}
        </Typo>
        <Typo element="p" type="bodySM" textColor="text-text-secondary">
          {help}
        </Typo>
      </div>
    </ResponsiveModal>
  );
};
